import type { PublicProductSummary } from '@repo/api-contracts';
import { Plus } from 'lucide-react';
import type { ReactElement } from 'react';
import { Link } from 'react-router';
import { STORE_PATH } from '@/shared/config';
import { cn } from '@/shared/ui';

/** 진열대에 세우는 상품 사진 수 — 최대 일곱 장, 세 장보다 적으면 진열대 자체를 생략한다(한두 장은 허전하다). */
const MAX_LINEUP = 7;
const MIN_LINEUP = 3;

/**
 * 진열대 높낮이 — 가운데에서 멀어질수록 낮아지는 리듬. 몇 장이든 가운데를 기준으로 대칭이 되게
 * 가운데와의 거리(0·1·2·3)로 높이를 고른다. 일곱 장 기준으로 쓰던 고정 배열은 세 장일 때 계단이 됐다.
 */
const LINEUP_HEIGHTS_BY_DISTANCE: readonly [string, string, string, string] = [
  'h-56 sm:h-72',
  'h-48 sm:h-60',
  'h-40 sm:h-48',
  'h-32 sm:h-40',
];

const lineupHeight = (index: number, count: number): string => {
  const distance = Math.round(Math.abs(index - (count - 1) / 2));
  return distance >= 3
    ? LINEUP_HEIGHTS_BY_DISTANCE[3]
    : (LINEUP_HEIGHTS_BY_DISTANCE[distance] ?? LINEUP_HEIGHTS_BY_DISTANCE[0]);
};

interface HomeBrandBannerProps {
  /** 추천 목록 — 대표사진이 있는 상품만 진열대에 세운다. 없으면 문구와 버튼만 남는다. */
  readonly products: readonly PublicProductSummary[];
}

/**
 * HOME 마무리 브랜드 배너 — 연한 그라데이션 판 가운데 큰 두 줄 문구와 스토어 보기,
 * 그 아래 추천 상품 대표사진을 가운데가 높고 양끝이 낮게 대칭으로 세운 진열대(장식).
 * 사진이 세 장보다 적으면 진열대를 생략하고 문구와 버튼만 남긴다.
 */
export const HomeBrandBanner = ({ products }: HomeBrandBannerProps): ReactElement => {
  const photos = products
    .flatMap((product) => (product.thumbnail !== null ? [product.thumbnail.url] : []))
    .slice(0, MAX_LINEUP);
  const lineup = photos.length >= MIN_LINEUP ? photos : [];

  return (
    <section
      aria-labelledby="home-brand-heading"
      className="overflow-hidden bg-gradient-to-b from-muted/30 via-background to-muted/80"
    >
      <div
        className={cn(
          'flex flex-col items-center px-5 pt-24 text-center sm:px-8 sm:pt-32',
          lineup.length === 0 && 'pb-24 sm:pb-32',
        )}
      >
        <h2
          id="home-brand-heading"
          className="text-[clamp(2rem,3.6vw,3.5rem)] leading-[1.2] font-medium tracking-[-0.02em]"
        >
          Custom goods for every team,
          <br />
          made by SuperStructure
        </h2>
        <p className="mt-4 text-[15px] text-muted-foreground sm:text-base">
          상품 사진 위에서 디자인하고, 상담 후 확정견적으로 주문마다 제작합니다.
        </p>
        <Link
          to={STORE_PATH}
          className="mt-8 inline-flex h-12 items-center gap-2 rounded-full border border-foreground px-6 text-[15px] font-medium transition-colors hover:bg-foreground hover:text-background"
        >
          스토어 보기
          <Plus aria-hidden strokeWidth={1.75} className="size-4" />
        </Link>
      </div>

      {lineup.length > 0 ? (
        // 진열대 — 푸터에 바로 닿지 않게 아래 여백을 두고, 사진들이 한 바닥선에 서도록 아래 정렬한다.
        <ul
          aria-hidden
          className="mx-auto mt-16 flex max-w-6xl items-end justify-center gap-3 px-5 pb-20 sm:mt-20 sm:gap-5 sm:px-8 sm:pb-28"
        >
          {lineup.map((url, index) => (
            <li
              key={url}
              className={cn(
                'aspect-[3/4] shrink-0 overflow-hidden bg-background shadow-[0_24px_48px_-24px_rgba(0,0,0,0.25)]',
                lineupHeight(index, lineup.length),
                // 좁은 화면에서는 양 끝(가운데에서 세 칸 떨어진 장)을 접어 다섯 장까지만 세운다.
                Math.abs(index - (lineup.length - 1) / 2) >= 3 && 'hidden md:block',
              )}
            >
              <img src={url} alt="" loading="lazy" className="size-full object-cover" />
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
};
